<template>
  <div class="row">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title">Feed In Management</h4>
          <p class="card-category">Review and manage incoming feed offers</p>
        </div>
        <div class="card-body">
          <!-- Tabs -->
          <b-tabs v-model="activeTab" content-class="mt-3" @input="onTabChange">
            <b-tab title="Pending Review" :title-link-class="getTabClass(0)">
              <template #title>
                <i class="fas fa-clock mr-1"></i>
                Pending Review
                <b-badge v-if="pendingCount > 0" variant="warning" class="ml-2">{{ pendingCount }}</b-badge>
              </template>
            </b-tab>
            <b-tab title="Uncategorized" :title-link-class="getTabClass(1)">
              <template #title>
                <i class="fas fa-question-circle mr-1"></i>
                Uncategorized
                <b-badge v-if="uncategorizedCount > 0" variant="info" class="ml-2">{{ uncategorizedCount }}</b-badge>
              </template>
            </b-tab>
            <b-tab title="Client Feeds" :title-link-class="getTabClass(2)">
              <template #title>
                <i class="fas fa-cogs mr-1"></i>
                Client Feeds
              </template>
            </b-tab>
          </b-tabs>

          <!-- Pending Review Tab Content -->
          <div v-show="activeTab === 0">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="text-muted">
                Offers from feeds that require manual approval before publishing
              </div>
              <b-form-radio-group
                v-model="pendingStatus"
                :options="pendingStatusOptions"
                button-variant="outline-primary"
                size="sm"
                buttons
                @input="fetchPendingOffers"
              ></b-form-radio-group>
            </div>
            <div class="table-responsive">
              <BTable
                :items="pendingOffers"
                :fields="pendingFields"
                :busy="isPendingBusy"
                hover
                show-empty
                empty-text="No pending offers found"
              >
                <template #table-busy>
                  <div class="text-center my-4">
                    <b-spinner class="align-middle"></b-spinner>
                    <span class="ml-2">Loading...</span>
                  </div>
                </template>
                <template #cell(feed)="{ item }">
                  <b-badge variant="secondary">{{ item.feed_in.legacy_client }}</b-badge>
                </template>
                <template #cell(title)="{ item }">
                  <router-link
                    :to="{
                      name: 'not-auto-publish-feed-offer',
                      params: { offerId: item.id }
                    }"
                  >
                    {{ item.contents[1] ? item.contents[1].title : item.contents[0].title }}
                  </router-link>
                </template>
                <template #cell(company)="{ item }">
                  <router-link
                    v-if="item.company"
                    :to="{
                      name: 'company',
                      params: { companyId: item.company.id }
                    }"
                  >
                    {{ item.company.name_fr }}
                  </router-link>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(jobboards)="{ item }">
                  <span class="text-muted small">
                    {{ listUnpublishedJobboardLabels(item.jobboards) }}
                  </span>
                </template>
                <template #cell(created_at)="{ item }">
                  <span class="small">{{ formatDate(item.created_at) }}</span>
                </template>
              </BTable>
              <BPagination
                v-if="pendingTotal > pendingPerPage"
                :disabled="isPendingBusy"
                v-model="pendingCurrentPage"
                @input="fetchPendingOffers"
                :total-rows="pendingTotal"
                :per-page="pendingPerPage"
                align="center"
              ></BPagination>
            </div>
          </div>

          <!-- Uncategorized Tab Content -->
          <div v-show="activeTab === 1">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="text-muted">
                Offers that need a category assigned before publishing
              </div>
            </div>
            <div class="table-responsive">
              <BTable
                :items="uncategorizedOffers"
                :fields="uncategorizedFields"
                :busy="isUncategorizedBusy"
                hover
                show-empty
                empty-text="No uncategorized offers found"
              >
                <template #table-busy>
                  <div class="text-center my-4">
                    <b-spinner class="align-middle"></b-spinner>
                    <span class="ml-2">Loading...</span>
                  </div>
                </template>
                <template #cell(title)="{ item }">
                  <strong>{{ item.contents.title }}</strong>
                </template>
                <template #cell(company)="{ item }">
                  {{ item.company.name_fr }}
                </template>
                <template #cell(apply_external_link)="{ item }">
                  <a target="_blank" :href="item.contents.apply_external_link" class="btn btn-sm btn-outline-secondary">
                    <i class="fas fa-external-link-alt"></i> View
                  </a>
                </template>
                <template #cell(actions)="{ item }">
                  <div class="d-flex flex-column" style="gap: 8px;">
                    <multiselect
                      v-model="item.category"
                      label="label"
                      placeholder="Select category..."
                      :options="categories"
                      @input="cleanJobboards(item)"
                    ></multiselect>
                    <multiselect
                      :disabled="item.category === null"
                      v-model="item.jobboards"
                      label="label"
                      placeholder="Select jobboards..."
                      :multiple="true"
                      :options="getJobboardOptions(item)"
                    ></multiselect>
                    <b-button
                      variant="success"
                      size="sm"
                      :disabled="item.category === null || item.jobboards.length === 0"
                      @click="handlePublish(item)"
                    >
                      <i class="fas fa-check mr-1"></i> Publish
                    </b-button>
                  </div>
                </template>
              </BTable>
            </div>
          </div>

          <!-- Client Feeds Tab Content -->
          <div v-show="activeTab === 2">
            <ClientFeedsTab />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BTable, BSpinner, BPagination, BFormRadioGroup, BTabs, BTab, BBadge, BButton } from "bootstrap-vue";
import Multiselect from "vue-multiselect";
import ClientFeedsTab from "./ClientFeedsTab.vue";

export default {
  components: {
    BTable,
    BSpinner,
    BPagination,
    BFormRadioGroup,
    BTabs,
    BTab,
    BBadge,
    Multiselect,
    BButton,
    ClientFeedsTab
  },
  data() {
    return {
      activeTab: 0,

      // Pending Review tab state
      pendingOffers: [],
      pendingCurrentPage: 1,
      pendingPerPage: 15,
      pendingTotal: 0,
      pendingCount: 0,
      isPendingBusy: true,
      pendingStatus: "pending",
      pendingStatusOptions: [
        { text: "All", value: "all" },
        { text: "Pending", value: "pending" }
      ],
      pendingFields: [
        { key: "feed", label: "Source" },
        { key: "title", label: "Title" },
        { key: "company", label: "Company" },
        { key: "jobboards", label: "Jobboards" },
        { key: "created_at", label: "Date" }
      ],

      // Uncategorized tab state
      uncategorizedOffers: [],
      uncategorizedCount: 0,
      isUncategorizedBusy: true,
      categories: [],
      uncategorizedFields: [
        { key: "title", label: "Title" },
        { key: "company", label: "Company" },
        { key: "apply_external_link", label: "Link" },
        { key: "actions", label: "Category & Publish", thStyle: { width: "40%" } }
      ]
    };
  },
  created() {
    this.fetchPendingOffers();
    this.fetchUncategorizedOffers();
    this.fetchCategories();
  },
  methods: {
    getTabClass(index) {
      return this.activeTab === index ? 'active' : '';
    },

    onTabChange(tabIndex) {
      if (tabIndex === 0 && this.pendingOffers.length === 0) {
        this.fetchPendingOffers();
      } else if (tabIndex === 1 && this.uncategorizedOffers.length === 0) {
        this.fetchUncategorizedOffers();
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString();
    },

    // Pending Review methods
    fetchPendingOffers() {
      this.isPendingBusy = true;
      this.$http
        .get(`/enroll_admin/not-auto-publish-feed-offers`, {
          params: {
            page: this.pendingCurrentPage,
            per_page: this.pendingPerPage,
            status: this.pendingStatus !== "all" ? this.pendingStatus : null
          }
        })
        .then(({ data }) => {
          this.pendingCurrentPage = data.current_page;
          this.pendingTotal = data.total;
          this.pendingOffers = data.data;
          if (this.pendingStatus === "pending") {
            this.pendingCount = data.total;
          }
        })
        .finally(() => {
          this.isPendingBusy = false;
        });
    },

    listUnpublishedJobboardLabels(jobboards) {
      return jobboards
        .filter(jobboard => jobboard.pivot.status !== "published")
        .map(jobboard => jobboard.label)
        .join(", ");
    },

    // Uncategorized methods
    fetchUncategorizedOffers() {
      this.isUncategorizedBusy = true;
      this.$http
        .get(`/enroll_admin/metro-unspecialized-feed-in`)
        .then(({ data }) => {
          this.uncategorizedOffers = data.map(offer => ({
            ...offer,
            contents: offer.contents[0],
            category: null,
            jobboards: []
          }));
          this.uncategorizedCount = data.length;
        })
        .finally(() => {
          this.isUncategorizedBusy = false;
        });
    },

    fetchCategories() {
      this.$http.get("/offer-attributes").then(({ data }) => {
        this.categories = data.category.map(category => ({
          ...category,
          label: category.label.fr
        }));
      });
    },

    cleanJobboards(item) {
      item.jobboards = [];
    },

    getJobboardOptions(item) {
      if (item.category) {
        if (item.category.jobboards.length === 1) {
          item.jobboards = item.category.jobboards;
        }
      }
      return item.category ? item.category.jobboards : [];
    },

    handlePublish(item) {
      this.$bvModal
        .msgBoxConfirm(
          `${item.category.label} → ${item.jobboards
            .map(jobboard => jobboard.label)
            .join(", ")}`,
          {
            size: "lg",
            title: `Publish: ${item.contents.title}`,
            okTitle: "Publish",
            okVariant: "success",
            cancelTitle: "Cancel"
          }
        )
        .then(confirm => {
          if (confirm) {
            this.publish(item);
          }
        });
    },

    publish(item) {
      this.isUncategorizedBusy = true;
      this.$http
        .put(`/enroll_admin/metro-unspecialized-feed-in/${item.id}/publish`, {
          jobboards: item.jobboards.map(({ id }) => id),
          category: item.category.id
        })
        .then(() => {
          this.fetchUncategorizedOffers();
        });
    }
  }
};
</script>

<style scoped>
.card-category {
  color: #9a9a9a;
  font-size: 14px;
}

.nav-tabs .nav-link {
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  border-bottom: 2px solid #51cbce;
}
</style>
